// Pages that are steps of one job share one sidebar item and a row of tabs at the top
// (docs/plans/nav-tabs.md). Each tab keeps its own address, like a restaurant's Orders,
// Tables and Kitchen. This file says which tabs a section has for this user and business;
// the sidebar's items stand for the same addresses (shell.tsx `also`).
import type { MemberRole } from '@ims/remote'
import { roleAtLeast } from '../workspace.tsx'

export type Section = 'sales' | 'purchase' | 'bills' | 'menu'

export interface SectionTabDef {
  to: string
  label: string
  /** Whether this tab is the page at this address (default: the path is `to`). */
  match?: (pathname: string, search: string) => boolean
}

export interface SectionAccess {
  role: MemberRole
  restaurant: boolean
  /** A restaurant whose products are its menu (business type restaurant). */
  menu: boolean
  purchaseControl: boolean
  /** Approval steps are set up (and purchase control is on). */
  approvals: boolean
  /** The local database is there: What to order needs it. */
  local: boolean
}

const categoriesTab = (search: string) => new URLSearchParams(search).get('tab') === 'categories'
const reorder = (search: string) => new URLSearchParams(search).get('show') === 'reorder'

/** The tabs this user sees in a section, in order. One tab or none means no tab bar. */
export function sectionTabs(section: Section, a: SectionAccess): SectionTabDef[] {
  const waiter = a.role === 'waiter'
  switch (section) {
    case 'sales':
      // A restaurant has no quotations or sales orders: its Invoices stands alone.
      if (a.restaurant) return []
      return [
        { to: '/invoices', label: 'Invoices' },
        ...(waiter
          ? []
          : [
              { to: '/orders', label: 'Orders' },
              { to: '/quotations', label: 'Quotations' },
            ]),
      ]
    case 'purchase':
      return [
        {
          to: '/purchase-orders',
          label: 'Orders',
          match: (p, s) => p === '/purchase-orders' && !reorder(s),
        },
        ...(a.local
          ? [
              {
                to: '/purchase-orders?show=reorder',
                label: 'What to order',
                match: (p: string, s: string) => p === '/purchase-orders' && reorder(s),
              },
            ]
          : []),
        ...(a.purchaseControl && roleAtLeast(a.role, 'staff')
          ? [{ to: '/purchase-requests', label: 'Requests' }]
          : []),
        ...(a.approvals && roleAtLeast(a.role, 'manager')
          ? [{ to: '/approvals', label: 'Approvals' }]
          : []),
      ]
    case 'bills':
      return [
        { to: '/purchases', label: 'Received' },
        { to: '/payments-made', label: 'Payments made' },
      ]
    case 'menu':
      if (!a.restaurant) return []
      return [
        {
          to: '/products',
          label: a.menu ? 'Menu' : 'Products',
          match: (p, s) => p === '/products' && !categoriesTab(s),
        },
        // A restaurant's categories are its menu's sections (docs/plans/menu-sections.md).
        ...(a.menu
          ? [
              {
                to: '/products?tab=categories',
                label: 'Categories',
                match: (p: string, s: string) => p === '/products' && categoriesTab(s),
              },
            ]
          : []),
        { to: '/ingredients', label: 'Ingredients' },
      ]
  }
}

/** Whether a tab is the page at this address. */
export function tabActive(tab: SectionTabDef, pathname: string, search: string): boolean {
  return tab.match ? tab.match(pathname, search) : pathname === tab.to
}
